import { STRINGS } from '@/strings/strings';
import type { PreviewTextCorridor } from '../../types';

/**
 * Per-page character counter (Bau-Spez v3.1 §6 line 317).
 *
 * "Die Oberfläche zeigt den Zeichenstand, durchgesetzt wird
 *  serverseitig (G7)."
 *
 * Display only. The server rejects an edit that falls outside the
 * corridor; this counter's colour is a client-side signal so the
 * customer sees WHY a save would fail before the round-trip.
 *
 * Bounds come from `envelope.textCorridor` since sister session's
 * `8bc4418` (envelope-fields correction), prop-drilled through
 * `PageEditRow` from `GeschichteScreen`. Server is the single source
 * of truth per G7 + Sebastian ruling 2026-08-05 §5c-5.
 *
 * ═══ OVER-LIMIT VARIANT (Block 2 commit 3, 2026-08-31) ═════════════
 *
 * When `value > corridor.max`, the label switches to
 * `counterLabelOverLimit` — reads as a note ("etwas zu lang"), not
 * an error. Sebastian ruling verbatim: "a scene slightly over the
 * corridor is not a failure." A value at or below `corridor.max`
 * uses the plain `counterLabel`.
 *
 * ═══ BELOW-MIN IS NEUTRAL (Block 2 c4, Sebastian ruling R2 2026-08-31) ═
 *
 * The red state fires ONLY above the max. Below the min, the counter
 * stays neutral. Sebastian's rationale verbatim: "A customer mid-edit
 * passes through short lengths on the way to a longer text; colouring
 * that red punishes typing." The server still enforces the floor
 * (throws `scene_text_too_short` on Speichern below
 * `scene_text_min_chars`); the customer sees the error via the
 * response, not via a typing-through-shortness warning. Pre-c4 the
 * counter turned red at any value outside the corridor; that punished
 * every customer on today's generator distribution because the
 * generator emits scenes below the pre-c4 floor of 120 for 95 % of
 * scenes. Corridor now [60, 200]; unedited scenes render neutral by
 * construction.
 */

export interface CharacterCounterProps {
  readonly value: number;
  readonly corridor: PreviewTextCorridor;
}

export function CharacterCounter({ value, corridor }: CharacterCounterProps) {
  const overLimit = value > corridor.max;
  // Position-1 Commit-3 addendum (Sebastian ruling 2026-09-03 late-
  // evening) — colour a POSITIVE signal too, not only a warning.
  // Three states: neutral below min (typing-through-shortness stays
  // uncoloured per Block 2 c4), GREEN within [min, max] (the save
  // will succeed), red above max (over cap). No new string —
  // colour only.
  const inRange = value >= corridor.min && value <= corridor.max;
  const template = overLimit
    ? STRINGS.book.geschichte.counterLabelOverLimit
    : STRINGS.book.geschichte.counterLabel;
  const label = template.replace('{n}', value.toString()).replace('{max}', corridor.max.toString());
  const colourClass = overLimit ? 'text-error' : inRange ? 'text-success' : 'text-dream-navy/70';
  return (
    <p className={`text-right text-xs ${colourClass}`} aria-live="polite">
      {label}
    </p>
  );
}
